import type { SyntheticEvent } from 'react';
import {
  AttachmentIcon,
  CalendarIcon,
  DocumentIcon,
  EmptyState,
  InfiniteScrollTrigger,
  ListPanel,
  MessageList,
  MessageListItem,
  MessageListItemSkeleton,
  SearchField,
  Tab,
  TabLabel,
  Tabs,
  Tag,
} from '@all-manager/design-system-v2';
import type { InboxEmail, InboxEmailStatus } from '../../types/inbox.types';
import { TAB_LABELS, useInboxList } from './useInboxList';

type Props = {
  emails: InboxEmail[];
  isLoading: boolean;
  selectedEmailId: string | null;
  onSelectEmail: (id: string) => void;
  currentTab: InboxEmailStatus;
  onTabChange: (tab: InboxEmailStatus) => void;
  pendingCount: number;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  isLoadMoreError: boolean;
  onLoadMore: () => void;
};

const SKELETON_ROWS = [0, 1, 2, 3, 4];

export default function InboxList(props: Props) {
  const hook = useInboxList(props);

  function handleTabChange(_event: SyntheticEvent, tab: InboxEmailStatus) {
    props.onTabChange(tab);
  }

  const header = (
    <Tabs divider value={props.currentTab} onChange={handleTabChange} aria-label="Statut des e-mails">
      <Tab
        value="pending"
        label={(
          <TabLabel
            label={TAB_LABELS.pending}
            count={props.pendingCount}
            countLabel={`${props.pendingCount} e-mail${props.pendingCount > 1 ? 's' : ''} à traiter`}
          />
        )}
      />
      <Tab value="processed" label={TAB_LABELS.processed} />
      <Tab value="ignored" label={TAB_LABELS.ignored} />
    </Tabs>
  );

  const toolbar = (
    <SearchField
      value={props.searchTerm}
      onChange={props.onSearchChange}
      label="Rechercher un e-mail"
      placeholder="Expéditeur, objet, contenu…"
    />
  );

  function renderContent() {
    if (props.isLoading) {
      return (
        <MessageList aria-label={hook.listLabel} aria-busy>
          {SKELETON_ROWS.map((row) => (
            <MessageListItemSkeleton key={row} />
          ))}
        </MessageList>
      );
    }

    if (hook.rows.length === 0) {
      return <EmptyState title={hook.emptyState.title} subtitle={hook.emptyState.subtitle} />;
    }

    return (
      <>
        <MessageList aria-label={hook.listLabel}>
          {hook.rows.map((row) => (
            <MessageListItem
              key={row.id}
              sender={row.sender}
              subject={row.subject}
              preview={row.preview}
              date={row.date}
              dateTime={row.dateTime}
              unread={row.unread}
              selected={row.id === props.selectedEmailId}
              onClick={function handleSelect() {
                props.onSelectEmail(row.id);
              }}
              tags={
                row.quoteLabel || row.hasEvent || row.attachmentsCount > 0 ? (
                  <>
                    {row.quoteLabel ? (
                      <Tag color="documents" icon={<DocumentIcon />} label={row.quoteLabel} />
                    ) : null}
                    {row.hasEvent ? <Tag color="rendezVous" icon={<CalendarIcon />} label="Rendez-vous" /> : null}
                    {row.attachmentsCount > 0 ? (
                      <Tag color="default" icon={<AttachmentIcon />} label={`${row.attachmentsCount} PJ`} />
                    ) : null}
                  </>
                ) : undefined
              }
            />
          ))}
        </MessageList>
        <InfiniteScrollTrigger
          hasMore={props.hasMore}
          isLoading={props.isFetchingNextPage}
          isError={props.isLoadMoreError}
          onLoadMore={props.onLoadMore}
          label="Charger plus d'e-mails"
        />
      </>
    );
  }

  return (
    <ListPanel fill header={header} toolbar={toolbar}>
      {renderContent()}
    </ListPanel>
  );
}
